import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import DashboardLayout from "../components/DashboardLayout";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { Key, Plus, Copy, Trash2, AlertTriangle, Code, Loader2, CheckCircle } from "lucide-react";

export default function APIKeysPage() {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [keyName, setKeyName] = useState("");
  const [creating, setCreating] = useState(false);
  const [newKey, setNewKey] = useState(null);
  const [hasAccess, setHasAccess] = useState(true);

  useEffect(() => {
    fetchAPIKeys();
  }, []);

  const fetchAPIKeys = async () => {
    try {
      const response = await axios.get(`${API}/api-keys`, { withCredentials: true });
      if (response.data.error) {
        setHasAccess(false);
        setKeys([]);
      } else {
        setKeys(response.data);
        setHasAccess(true);
      }
    } catch (error) {
      if (error.response?.status !== 401) {
        toast.error("Failed to load API keys");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!keyName) return;
    
    setCreating(true);
    try {
      const response = await axios.post(`${API}/api-keys`, {
        name: keyName,
        permissions: ["shoots:create", "shoots:read", "assets:read"]
      }, { withCredentials: true });
      
      setNewKey(response.data);
      setKeyName("");
      fetchAPIKeys();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create API key");
      setIsDialogOpen(false);
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (keyId) => {
    if (!window.confirm("Are you sure you want to revoke this API key? This action cannot be undone.")) return;
    
    try {
      await axios.delete(`${API}/api-keys/${keyId}`, { withCredentials: true });
      toast.success("API key revoked");
      fetchAPIKeys();
    } catch (error) {
      toast.error("Failed to revoke API key");
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-neutral-900">API Keys</h1>
            <p className="text-neutral-600">Manage API access for programmatic integration</p>
          </div>
          {hasAccess && (
            <Dialog open={isDialogOpen} onOpenChange={(open) => {
              setIsDialogOpen(open);
              if (!open) setNewKey(null);
            }}>
              <DialogTrigger asChild>
                <Button className="bg-indigo-700 hover:bg-indigo-800 gap-2">
                  <Plus className="w-4 h-4" /> Create API Key
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{newKey ? "API Key Created!" : "Create New API Key"}</DialogTitle>
                </DialogHeader>
                
                {newKey ? (
                  <div className="space-y-4 mt-4">
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                        <div>
                          <p className="font-semibold text-green-900">Save your API key now!</p>
                          <p className="text-sm text-green-700">This key will only be shown once. Copy it and store it securely.</p>
                        </div>
                      </div>
                    </div>
                    
                    <div>
                      <label className="text-sm font-medium text-neutral-700 mb-1 block">Your API Key</label>
                      <div className="flex gap-2">
                        <Input value={newKey.key} readOnly className="font-mono text-sm" />
                        <Button variant="outline" onClick={() => copyToClipboard(newKey.key)}>
                          <Copy className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    
                    <Button onClick={() => { setIsDialogOpen(false); setNewKey(null); }} className="w-full">
                      Done
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={handleCreate} className="space-y-4 mt-4">
                    <div>
                      <label className="text-sm font-medium text-neutral-700 mb-1 block">Key Name</label>
                      <Input
                        value={keyName}
                        onChange={(e) => setKeyName(e.target.value)}
                        placeholder="e.g., Production API Key"
                        required
                      />
                      <p className="text-xs text-neutral-500 mt-1">A friendly name to identify this key</p>
                    </div>
                    
                    <div>
                      <label className="text-sm font-medium text-neutral-700 mb-1 block">Permissions</label>
                      <div className="space-y-2 text-sm text-neutral-600 bg-neutral-50 rounded-lg p-3">
                        <div className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-green-600" />
                          <span>Create AI shoots (shoots:create)</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-green-600" />
                          <span>Read shoot status (shoots:read)</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-green-600" />
                          <span>Download assets (assets:read)</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex justify-end gap-2 pt-4">
                      <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                        Cancel
                      </Button>
                      <Button type="submit" disabled={creating} className="bg-indigo-700 hover:bg-indigo-800">
                        {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Key className="w-4 h-4 mr-2" />}
                        Create Key
                      </Button>
                    </div>
                  </form>
                )}
              </DialogContent>
            </Dialog>
          )}
        </div>

        {!hasAccess && (
          <Card className="bg-amber-50 border-amber-200">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <AlertTriangle className="w-6 h-6 text-amber-600" />
                <div>
                  <h3 className="font-semibold text-amber-900 mb-2">Studio Plan Required</h3>
                  <p className="text-amber-800 mb-4">
                    API access is available exclusively for Studio plan subscribers. Upgrade your plan to integrate Jini Studio with your applications.
                  </p>
                  <Button className="bg-amber-600 hover:bg-amber-700 text-white">
                    Upgrade to Studio Plan
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {hasAccess && (
          <>
            {/* API Documentation */}
            <Card className="bg-neutral-50 border-neutral-200">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <Code className="w-5 h-5 text-neutral-600 mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-neutral-900">API Documentation</h3>
                    <p className="text-sm text-neutral-600 mb-2">
                      Use these endpoints to integrate Jini Studio with your applications:
                    </p>
                    <div className="font-mono text-xs bg-white rounded p-2 border border-neutral-200 space-y-1">
                      <p><span className="text-green-600">GET</span> /api/v1/shoots - List all shoots</p>
                      <p><span className="text-blue-600">POST</span> /api/v1/shoots - Create new shoot</p>
                      <p><span className="text-green-600">GET</span> /api/v1/assets/&#123;shoot_id&#125; - Get shoot assets</p>
                    </div>
                    <p className="text-xs text-neutral-500 mt-2">
                      Include your API key in the <code className="bg-neutral-200 px-1 rounded">X-API-Key</code> header.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* API Keys List */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Key className="w-5 h-5" />
                  Your API Keys ({keys.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
                  </div>
                ) : keys.length === 0 ? (
                  <div className="text-center py-12">
                    <Key className="w-12 h-12 text-neutral-300 mx-auto mb-4" />
                    <p className="text-neutral-500 mb-4">No API keys created yet</p>
                    <Button variant="outline" onClick={() => setIsDialogOpen(true)}>
                      <Plus className="w-4 h-4 mr-2" /> Create Your First Key
                    </Button>
                  </div>
                ) : (
                  <div className="divide-y divide-neutral-100">
                    {keys.map((key) => (
                      <div key={key.api_key_id} className="flex items-center justify-between py-4">
                        <div>
                          <p className="font-medium text-neutral-900">{key.name}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <code className="text-sm bg-neutral-100 px-2 py-0.5 rounded font-mono">
                              {key.key_prefix}...
                            </code>
                            <span className="text-xs text-neutral-500">
                              Created {new Date(key.created_at).toLocaleDateString()}
                            </span>
                            {key.last_used_at && (
                              <span className="text-xs text-neutral-500">
                                • Last used {new Date(key.last_used_at).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <Badge variant="outline">{key.usage_count || 0} requests</Badge>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRevoke(key.api_key_id)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
